import { ErrorCode, IGetManifest, PaymentType } from '@ace-clublabs/payment-types';
import { NavigateFunction } from 'react-router-dom';
import { Pages } from '../shared/pages';

export type PaymentFormBackTarget = 'payment-methods' | 'exit-skipped' | 'exit-previous';

export function hasManifestSoftError(manifest?: IGetManifest): boolean {
  return !!(manifest?.softError?.code ?? manifest?.softErrorCode);
}

export function getManifestSoftErrorCode(
  manifest?: IGetManifest,
  softErrorCodeProp?: ErrorCode
): ErrorCode | undefined {
  if (softErrorCodeProp) return softErrorCodeProp;
  return manifest?.softError?.code ?? manifest?.softErrorCode;
}

export function getSoftErrorPaymentType(manifest?: IGetManifest): PaymentType | undefined {
  return manifest?.softError?.paymentType ?? manifest?.paymentType;
}

const CARD_OR_ECHECK_TYPES = [
  PaymentType.CreditOrDebit,
  PaymentType.DebitOnly,
  PaymentType.Prepaid,
  PaymentType.Echeck,
];

export function isCardOrEcheckPaymentType(paymentType?: PaymentType): boolean {
  return !!paymentType && CARD_OR_ECHECK_TYPES.includes(paymentType);
}

/** Banner on PaymentMethods only for wallet declines and stored-payment declines (no entry form). */
export function shouldShowSoftErrorBannerOnPaymentMethods(manifest?: IGetManifest): boolean {
  if (!hasManifestSoftError(manifest)) return false;
  if (manifest?.softError?.isStoredPayment) return true;

  const paymentType = getSoftErrorPaymentType(manifest);
  return paymentType === PaymentType.Apple || paymentType === PaymentType.Google;
}

/** One-time redirect to PaymentPage for card/eCheck soft errors; skipped after user returns via Back. */
export function shouldRedirectSoftErrorToPaymentForm(
  manifest: IGetManifest | undefined,
  softErrorFormRedirectDone: boolean
): boolean {
  if (!hasManifestSoftError(manifest) || softErrorFormRedirectDone) return false;
  if (manifest?.softError?.isStoredPayment) return false;

  return isCardOrEcheckPaymentType(getSoftErrorPaymentType(manifest));
}

export function resolvePaymentFormBackTarget(
  manifest: IGetManifest | undefined,
  params: {
    softErrorCode?: ErrorCode;
    paymentMethodsSkipped?: boolean;
  }
): PaymentFormBackTarget {
  const { softErrorCode, paymentMethodsSkipped } = params;

  if (getManifestSoftErrorCode(manifest, softErrorCode)) {
    return 'payment-methods';
  }

  if (paymentMethodsSkipped) return 'exit-skipped';
  return 'exit-previous';
}

export function navigatePaymentFormBack(
  navigate: NavigateFunction,
  manifest: IGetManifest | undefined,
  params: {
    softErrorCode?: ErrorCode;
    paymentMethodsSkipped?: boolean;
    onClearPaymentMethodsSkipped?: () => void;
  }
): void {
  const { onClearPaymentMethodsSkipped, ...resolveParams } = params;
  const target = resolvePaymentFormBackTarget(manifest, resolveParams);

  switch (target) {
    case 'payment-methods':
      navigate(Pages.PaymentMethods);
      break;
    case 'exit-skipped':
      onClearPaymentMethodsSkipped?.();
      navigate(-2);
      break;
    case 'exit-previous':
      navigate(-1);
      break;
  }
}

export function getSoftErrorBannerMessage(errorCode: ErrorCode): string {
  switch (errorCode) {
    case ErrorCode.ChargeDeclined:
      return 'Your payment was declined. Please try a different payment method.';
    case ErrorCode.InsufficientFunds:
      return 'Insufficient funds. Please try a different payment method.';
    case ErrorCode.CardTypeNotAllowed:
    case ErrorCode.CreditCardNotAllowed:
    case ErrorCode.DebitCardNotAllowed:
    case ErrorCode.PrepaidCardNotAllowed:
      return 'This payment method is not accepted. Please try a different one.';
    case ErrorCode.PrepaidCardNotAllowedForAutoPay:
      return 'Prepaid cards are not accepted, please try a different card.';
    case ErrorCode.InvalidCard:
    case ErrorCode.InvalidToken:
    case ErrorCode.InvalidAccountNumber:
    case ErrorCode.InvalidBankAccountOrRoutingNumber:
      return 'Invalid payment method. Please try a different one.';
    case ErrorCode.NetworkTimeout:
      return 'A network error occurred. Please try again.';
    default:
      return 'There was an issue with your payment. Please try a different payment method.';
  }
}
